/**
 * Drone pad synth using Web Audio API.
 *
 * Each voice supports N oscillators with per-oscillator waveform, harmonic
 * ratio, detune, gain, and stereo pan. Optional sub-oscillator, noise source,
 * and amplitude LFO enable distinct worship pad characters.
 *
 * Detuned oscillators MUST be spread across the stereo field: summed to mono,
 * a symmetric detune trio periodically phase-cancels, pumping the level by
 * ~6 dB (measured). Panned apart, the same beating reads as stereo width and
 * movement — the classic string-machine ensemble effect.
 *
 * Signal chain per voice:
 *   oscillators (each with individual gain -> pan)
 *   + sub-oscillator (sine @ freq/2)        --> HP -> LP (with filter LFO) -> [amp LFO] -> envelope
 *   + noise (looped buffer -> bandpass)
 *   (HP/LP are stereo biquads; sub and noise stay centered)
 */

import { getNativeAudioContext, getTone } from "./get-native-context";

export interface OscDef {
  waveform: OscillatorType;
  /** Frequency multiplier (1 = fundamental, 2 = octave up, 0.5 = octave down) */
  ratio: number;
  /** Detune in cents */
  detune: number;
  /** Linear gain (0-1) */
  gain: number;
  /** Stereo position (-1 left .. 1 right, 0/omitted = center) */
  pan?: number;
}

export interface DronePadConfig {
  oscillators: OscDef[];

  /** Sub-octave sine level (linear, 0 = off) */
  subOscGain: number;
  /** Noise layer level (linear, 0 = off) */
  noiseGain: number;
  /** Bandpass [lowHz, highHz] for noise shaping */
  noiseBandwidth: [number, number];

  filterCutoff: number;
  filterHPCutoff: number;
  filterQ: number;
  filterLfoRate: number;
  /** Filter LFO depth (0-1), modulates cutoff by cutoff * depth */
  filterLfoDepth: number;

  /** Amplitude LFO rate in Hz (0 = off) */
  ampLfoRate: number;
  /** Amplitude LFO depth (0-1), gain oscillates between (1-depth) and 1.0 */
  ampLfoDepth: number;

  /**
   * Per-oscillator slow pitch drift, peak cents (0 = off). Static detune
   * between oscillators is a constant-rate sweeping comb — a flanger. A slow
   * independent drift on each oscillator keeps the same average detune but
   * stops the comb repeating: measured harmonic-swing periodicity 0.94 → 0.21.
   */
  driftDepth: number;
  /** Nominal drift rate in Hz; each oscillator gets its own spread of this. */
  driftRate: number;

  attack: number;
  release: number;
  /** Output volume in dB */
  volume: number;
}

export const DEFAULT_PAD_CONFIG: DronePadConfig = {
  oscillators: [
    { waveform: "sawtooth", ratio: 1, detune: -12, gain: 1 / 3, pan: -0.8 },
    { waveform: "sawtooth", ratio: 1, detune: 0, gain: 1 / 3, pan: 0 },
    { waveform: "sawtooth", ratio: 1, detune: 12, gain: 1 / 3, pan: 0.8 },
  ],
  subOscGain: 0,
  noiseGain: 0,
  noiseBandwidth: [200, 2000],
  filterCutoff: 2000,
  filterHPCutoff: 20,
  filterQ: 1,
  filterLfoRate: 0.15,
  filterLfoDepth: 0.4,
  ampLfoRate: 0,
  ampLfoDepth: 0,
  driftDepth: 3,
  driftRate: 0.15,
  attack: 3,
  release: 5,
  volume: -12,
};

/**
 * Reference voice count for loudness normalization. Voices are scaled by
 * sqrt(REF / count) so a 2-note voicing and a wide 11-note stack land at
 * comparable loudness (and the wide stack can't clip). 3 is the typical
 * root+fifth+octave voicing, so defaults keep their historical level.
 */
const VOICE_NORM_REF_COUNT = 3;

export class DronePad {
  private config: DronePadConfig;
  private voices: Map<string, DroneVoice> = new Map();
  private outputNode: GainNode | null = null;
  private normGain: GainNode | null = null;
  private _initialized = false;
  /**
   * Preset-authored envelope times, kept so a transition-time override can
   * scale attack and release together instead of flattening both to one
   * number — a preset's long release relative to its attack is part of its
   * character.
   */
  private baseAttack: number;
  private baseRelease: number;
  private transitionScale = 1;

  constructor(config?: Partial<DronePadConfig>) {
    this.config = { ...DEFAULT_PAD_CONFIG, ...config };
    this.baseAttack = this.config.attack;
    this.baseRelease = this.config.release;
  }

  /**
   * Scales the note fade times around the preset's own envelope. `scale` is
   * the transition time as a fraction of the reference transition length, so
   * 1 leaves every preset exactly as authored.
   */
  setTransitionScale(scale: number): void {
    this.transitionScale = scale;
    this.applyTransitionScale();
  }

  private applyTransitionScale(): void {
    // Live voices share this config object, so a note fading out after the
    // change releases on the new time too.
    this.config.attack = this.baseAttack * this.transitionScale;
    this.config.release = this.baseRelease * this.transitionScale;
  }

  async initialize(): Promise<GainNode> {
    if (this._initialized && this.outputNode) return this.outputNode;

    const ctx = await getNativeAudioContext();

    this.outputNode = ctx.createGain();
    this.outputNode.gain.value = dbToLinear(this.config.volume);

    this.normGain = ctx.createGain();
    this.normGain.gain.value = 1;
    this.normGain.connect(this.outputNode);

    this._initialized = true;
    return this.outputNode;
  }

  /**
   * Retarget the voice-count normalization. Smoothed so notes fading in or
   * out (range drags, key morphs) shift the level gently rather than stepping.
   * When the last voice is released we hold the current value so the release
   * tails keep their level.
   */
  private updateVoiceNorm(): void {
    if (!this.normGain || this.voices.size === 0) return;
    const target = Math.sqrt(VOICE_NORM_REF_COUNT / this.voices.size);
    const now = this.normGain.context.currentTime;
    this.normGain.gain.setTargetAtTime(target, now, 0.25);
  }

  async noteOn(note: string): Promise<void> {
    if (this.voices.has(note)) return;
    if (!this.outputNode) await this.initialize();

    const T = await getTone();
    const ctx = await getNativeAudioContext();
    if (!this.outputNode) return;

    const freq = T.Frequency(note).toFrequency();
    const voice = new DroneVoice(ctx, freq, this.config);
    voice.connect(this.normGain ?? this.outputNode);
    voice.start();
    this.voices.set(note, voice);
    this.updateVoiceNorm();
  }

  noteOff(note: string): void {
    const voice = this.voices.get(note);
    if (!voice) return;
    voice.stop();
    this.voices.delete(note);
    this.updateVoiceNorm();
  }

  allNotesOff(): void {
    for (const [, voice] of this.voices) {
      voice.stop();
    }
    this.voices.clear();
  }

  /**
   * Swap the full pad config (used for preset switching).
   * Active notes crossfade: old voices release while new ones attack.
   */
  async setConfig(config: DronePadConfig): Promise<void> {
    const activeNotes = [...this.voices.keys()];
    this.allNotesOff();
    this.config = {
      ...config,
      oscillators: config.oscillators.map((o) => ({ ...o })),
      noiseBandwidth: [config.noiseBandwidth[0], config.noiseBandwidth[1]],
    };
    this.baseAttack = this.config.attack;
    this.baseRelease = this.config.release;
    this.applyTransitionScale();

    if (this.outputNode) {
      const now = this.outputNode.context.currentTime;
      this.outputNode.gain.setTargetAtTime(dbToLinear(this.config.volume), now, 0.02);
    }

    for (const note of activeNotes) {
      await this.noteOn(note);
    }
  }

  getOutput(): GainNode | null {
    return this.outputNode;
  }

  setVolume(db: number): void {
    this.config.volume = db;
    if (this.outputNode) {
      // Short ramp instead of a raw .value set: browsers no longer dezipper,
      // so instant gain steps click audibly on a sustained pad.
      const now = this.outputNode.context.currentTime;
      this.outputNode.gain.setTargetAtTime(dbToLinear(db), now, 0.02);
    }
  }

  setFilterCutoff(hz: number): void {
    this.config.filterCutoff = hz;
    for (const voice of this.voices.values()) {
      voice.setFilterCutoff(hz);
    }
  }

  setFilterHPCutoff(hz: number): void {
    this.config.filterHPCutoff = hz;
    for (const voice of this.voices.values()) {
      voice.setFilterHPCutoff(hz);
    }
  }

  dispose(): void {
    this.allNotesOff();
    this.normGain?.disconnect();
    this.normGain = null;
    this.outputNode?.disconnect();
    this.outputNode = null;
    this._initialized = false;
  }
}

function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * A sine PeriodicWave starting at `phase` radians. OscillatorNodes always
 * start at phase 0, so this is the only way to give each drift LFO its own
 * starting point: sin(x + φ) = cos φ · sin x + sin φ · cos x.
 */
function phaseOffsetSine(ctx: AudioContext, phase: number): PeriodicWave {
  const real = new Float32Array([0, Math.sin(phase)]);
  const imag = new Float32Array([0, Math.cos(phase)]);
  return ctx.createPeriodicWave(real, imag);
}

// ---------------------------------------------------------------------------
// DroneVoice — per-note audio graph
// ---------------------------------------------------------------------------

class DroneVoice {
  private oscs: OscillatorNode[] = [];
  private oscGains: GainNode[] = [];
  private oscPanners: StereoPannerNode[] = [];
  /** Last node of each oscillator chain (gain, or panner when panned). */
  private oscTails: AudioNode[] = [];
  private driftLfos: OscillatorNode[] = [];
  private driftGains: GainNode[] = [];

  private subOsc: OscillatorNode | null = null;
  private subOscGainNode: GainNode | null = null;

  private noiseSource: AudioBufferSourceNode | null = null;
  private noiseHP: BiquadFilterNode | null = null;
  private noiseLP: BiquadFilterNode | null = null;
  private noiseGainNode: GainNode | null = null;

  private hpFilter: BiquadFilterNode;
  private lpFilter: BiquadFilterNode;

  private filterLfo: OscillatorNode;
  private filterLfoGain: GainNode;

  private ampLfo: OscillatorNode | null = null;
  private ampLfoScaler: GainNode | null = null;
  private ampLfoGainNode: GainNode | null = null;

  private envelope: GainNode;
  private ctx: AudioContext;
  private config: DronePadConfig;

  constructor(ctx: AudioContext, freq: number, config: DronePadConfig) {
    this.ctx = ctx;
    this.config = config;

    // -- Main oscillators --------------------------------------------------
    // Detuned oscillators are spread across the stereo field so their beating
    // becomes width instead of mono level-pumping (see file header).
    config.oscillators.forEach((def, i) => {
      const osc = ctx.createOscillator();
      osc.type = def.waveform;
      osc.frequency.value = freq * def.ratio;
      osc.detune.value = def.detune;

      if (config.driftDepth > 0) {
        // Rate and start phase are spread by oscillator index and by note, so
        // no two oscillators (in this voice or another) drift in lockstep.
        const spread = ((i * 0.37 + freq / 1000) % 1);
        const lfo = ctx.createOscillator();
        lfo.setPeriodicWave(phaseOffsetSine(ctx, i * 1.9 + freq / 50));
        lfo.frequency.value = config.driftRate * (0.7 + 0.6 * spread);

        const depth = ctx.createGain();
        depth.gain.value = config.driftDepth;
        lfo.connect(depth);
        depth.connect(osc.detune);

        this.driftLfos.push(lfo);
        this.driftGains.push(depth);
      }

      const g = ctx.createGain();
      g.gain.value = def.gain;
      osc.connect(g);

      let tail: AudioNode = g;
      if (def.pan) {
        const panner = ctx.createStereoPanner();
        panner.pan.value = Math.max(-1, Math.min(1, def.pan));
        g.connect(panner);
        this.oscPanners.push(panner);
        tail = panner;
      }

      this.oscs.push(osc);
      this.oscGains.push(g);
      this.oscTails.push(tail);
    });

    // -- Sub-oscillator (sine, one octave below) ---------------------------
    if (config.subOscGain > 0) {
      this.subOsc = ctx.createOscillator();
      this.subOsc.type = "sine";
      this.subOsc.frequency.value = freq / 2;

      this.subOscGainNode = ctx.createGain();
      this.subOscGainNode.gain.value = config.subOscGain;
      this.subOsc.connect(this.subOscGainNode);
    }

    // -- Noise source (looped white noise through bandpass) ----------------
    if (config.noiseGain > 0) {
      const bufLen = Math.round(ctx.sampleRate * 2);
      const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;

      this.noiseSource = ctx.createBufferSource();
      this.noiseSource.buffer = buf;
      this.noiseSource.loop = true;

      this.noiseHP = ctx.createBiquadFilter();
      this.noiseHP.type = "highpass";
      this.noiseHP.frequency.value = config.noiseBandwidth[0];
      this.noiseHP.Q.value = 0.7;

      this.noiseLP = ctx.createBiquadFilter();
      this.noiseLP.type = "lowpass";
      this.noiseLP.frequency.value = config.noiseBandwidth[1];
      this.noiseLP.Q.value = 0.7;

      this.noiseGainNode = ctx.createGain();
      this.noiseGainNode.gain.value = config.noiseGain;

      this.noiseSource.connect(this.noiseHP);
      this.noiseHP.connect(this.noiseLP);
      this.noiseLP.connect(this.noiseGainNode);
    }

    // -- HP filter ---------------------------------------------------------
    this.hpFilter = ctx.createBiquadFilter();
    this.hpFilter.type = "highpass";
    this.hpFilter.frequency.value = config.filterHPCutoff;
    this.hpFilter.Q.value = 0.7;

    // -- LP filter (with LFO modulation) -----------------------------------
    this.lpFilter = ctx.createBiquadFilter();
    this.lpFilter.type = "lowpass";
    this.lpFilter.frequency.value = config.filterCutoff;
    this.lpFilter.Q.value = config.filterQ;

    this.filterLfo = ctx.createOscillator();
    this.filterLfo.type = "sine";
    this.filterLfo.frequency.value = config.filterLfoRate;

    this.filterLfoGain = ctx.createGain();
    this.filterLfoGain.gain.value = config.filterCutoff * config.filterLfoDepth;
    this.filterLfo.connect(this.filterLfoGain);
    this.filterLfoGain.connect(this.lpFilter.frequency);

    // -- Amplitude LFO (optional, for "breathing" swell) -------------------
    if (config.ampLfoRate > 0 && config.ampLfoDepth > 0) {
      this.ampLfoGainNode = ctx.createGain();
      this.ampLfoGainNode.gain.value = 1 - config.ampLfoDepth / 2;

      this.ampLfo = ctx.createOscillator();
      this.ampLfo.type = "sine";
      this.ampLfo.frequency.value = config.ampLfoRate;

      this.ampLfoScaler = ctx.createGain();
      this.ampLfoScaler.gain.value = config.ampLfoDepth / 2;
      this.ampLfo.connect(this.ampLfoScaler);
      this.ampLfoScaler.connect(this.ampLfoGainNode.gain);
    }

    // -- Envelope ----------------------------------------------------------
    this.envelope = ctx.createGain();
    this.envelope.gain.value = 0;

    // -- Wiring ------------------------------------------------------------
    // All sources -> HP -> LP -> [ampLfo] -> envelope
    for (const tail of this.oscTails) tail.connect(this.hpFilter);
    this.subOscGainNode?.connect(this.hpFilter);
    this.noiseGainNode?.connect(this.hpFilter);

    this.hpFilter.connect(this.lpFilter);

    if (this.ampLfoGainNode) {
      this.lpFilter.connect(this.ampLfoGainNode);
      this.ampLfoGainNode.connect(this.envelope);
    } else {
      this.lpFilter.connect(this.envelope);
    }
  }

  connect(dest: AudioNode): void {
    this.envelope.connect(dest);
  }

  start(): void {
    const now = this.ctx.currentTime;
    for (const osc of this.oscs) osc.start(now);
    for (const lfo of this.driftLfos) lfo.start(now);
    this.subOsc?.start(now);
    this.noiseSource?.start(now);
    this.filterLfo.start(now);
    this.ampLfo?.start(now);

    this.envelope.gain.setValueAtTime(0, now);
    this.envelope.gain.linearRampToValueAtTime(1, now + this.config.attack);
  }

  stop(): void {
    const now = this.ctx.currentTime;
    this.envelope.gain.cancelScheduledValues(now);
    this.envelope.gain.setValueAtTime(this.envelope.gain.value, now);
    this.envelope.gain.linearRampToValueAtTime(0, now + this.config.release);

    const stopTime = now + this.config.release + 0.1;
    for (const osc of this.oscs) osc.stop(stopTime);
    for (const lfo of this.driftLfos) lfo.stop(stopTime);
    this.subOsc?.stop(stopTime);
    this.noiseSource?.stop(stopTime);
    this.filterLfo.stop(stopTime);
    this.ampLfo?.stop(stopTime);

    setTimeout(() => this.disconnectAll(), (this.config.release + 0.5) * 1000);
  }

  setFilterCutoff(hz: number): void {
    const now = this.ctx.currentTime;
    this.lpFilter.frequency.setTargetAtTime(hz, now, 0.02);
    this.filterLfoGain.gain.setTargetAtTime(hz * this.config.filterLfoDepth, now, 0.02);
  }

  setFilterHPCutoff(hz: number): void {
    this.hpFilter.frequency.setTargetAtTime(hz, this.ctx.currentTime, 0.02);
  }

  private disconnectAll(): void {
    this.envelope.disconnect();
    this.hpFilter.disconnect();
    this.lpFilter.disconnect();
    this.filterLfo.disconnect();
    this.filterLfoGain.disconnect();
    for (const osc of this.oscs) osc.disconnect();
    for (const g of this.oscGains) g.disconnect();
    for (const p of this.oscPanners) p.disconnect();
    for (const lfo of this.driftLfos) lfo.disconnect();
    for (const g of this.driftGains) g.disconnect();
    this.subOsc?.disconnect();
    this.subOscGainNode?.disconnect();
    this.noiseSource?.disconnect();
    this.noiseHP?.disconnect();
    this.noiseLP?.disconnect();
    this.noiseGainNode?.disconnect();
    this.ampLfo?.disconnect();
    this.ampLfoScaler?.disconnect();
    this.ampLfoGainNode?.disconnect();
  }
}
